import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/* ------------------------------------------------------------------ */
/*  Product → parent category mapping                                  */
/* ------------------------------------------------------------------ */
const SHADES = ['roller-shades', 'solar-shades', 'roman-shades', 'cellular-shades', 'zebra-shades'];
const DRAPERY = ['motorized-drapes', 'ripple-fold-drapes', 'sheer-drapes', 'pinch-pleat-drapes', 'grommet-drapes', 'side-panels'];
const MOTORS_CAT = ['motorized-shades', 'motorized-blinds', 'motorized-skylights', 'smart-home-integration'];

const productCategory = (slug) => {
  if (SHADES.includes(slug)) return { path: '/products/shades', name: 'Shades' };
  if (DRAPERY.includes(slug)) return { path: '/products/drapery', name: 'Drapery' };
  if (MOTORS_CAT.includes(slug)) return { path: '/motorization', name: 'Motorized Systems' };
  return null;
};

/* ------------------------------------------------------------------ */
/*  Human-readable labels for slugs                                    */
/* ------------------------------------------------------------------ */
const LOCATION_LABELS = { nyc: 'Motorized Shades in NYC', westchester: 'Motorized Shades in Westchester', 'greenwich-ct': 'Window Shades in Greenwich, CT' };

const LABELS = {
  'architects-designers': 'Architects & Designers',
  'blackout-vs-light-filtering-vs-solar-shades': 'Blackout vs Light Filtering vs Solar',
  about: 'About Us',
  products: 'Products',
  gallery: 'Gallery',
  trade: 'Trade Program',
  partners: 'Our Partners',
  motorization: 'Motorization',
  contact: 'Contact',
  faq: 'FAQ',
  fabrics: 'Fabrics',
  consultation: 'Free Consultation',
  blog: 'Blog',
  'service-area': 'Service Area',
  'measuring-installation': 'Measuring & Installation',
  'warranty-service': 'Warranty & Service',
  shades: 'Shades',
  drapery: 'Drapery',
  // Product detail
  'roller-shades': 'Custom Roller Shades',
  'solar-shades': 'Solar Shades',
  'roman-shades': 'Roman Shades',
  'cellular-shades': 'Cellular Shades',
  'zebra-shades': 'Zebra Shades',
  'motorized-drapes': 'Motorized Drapes',
  'ripple-fold-drapes': 'Ripple Fold Drapes',
  'sheer-drapes': 'Sheer Drapes',
  'pinch-pleat-drapes': 'Pinch Pleat Drapes',
  'grommet-drapes': 'Grommet Drapes',
  'side-panels': 'Side Panels',
  'motorized-shades': 'Motorized Shades',
  'motorized-blinds': 'Motorized Blinds',
  'motorized-skylights': 'Motorized Skylights',
  'smart-home-integration': 'Smart Home Integration',
  // Motorization detail
  'roller-motor': 'Roller Motor',
  'drapery-motor': 'Drapery Motor',
  'roman-shade-motor': 'Roman Shade Motor',
  'smart-connectivity': 'Smart Connectivity',
  // Smart connectivity sub-pages
  'works-with-motionblinds': 'Works with MotionBlinds',
  matter: 'Matter',
  hubs: 'Hubs',
  'built-in-motor-technology': 'Built-in Motor Technology',
  // Partner names
  somfy: 'Somfy',
  'rollease-acmeda': 'Rollease Acmeda',
  vertilux: 'Vertilux',
  lutron: 'Lutron',
  'hunter-douglas': 'Hunter Douglas',
  fabricut: 'Fabricut',
  control4: 'Control4',
  'lutron-homeworks': 'Lutron HomeWorks',
  coulisse: 'Coulisse',
  // Shades sub-routes
  'motorized-blackout': 'Motorized Blackout Shades',
};

/* ------------------------------------------------------------------ */
/*  Pages excluded from breadcrumbs (no semantic value)                */
/* ------------------------------------------------------------------ */
const EXCLUDED_PREFIXES = [
  '/admin', '/login', '/register', '/forgot-password', '/reset-password',
  '/verify-email', '/auth', '/checkout', '/dashboard', '/my-quotes',
  '/my-orders', '/my-favorites', '/saved', '/saved-configs', '/installer',
  '/customer-portal', '/compare-fabrics', '/preview/', '/feedback/',
  '/track/', '/pay/', '/st120', '/request-samples', '/track-samples',
  '/products/compare',
];

/* ------------------------------------------------------------------ */
/*  Build crumbs from path                                             */
/* ------------------------------------------------------------------ */
export const buildCrumbs = (pathname) => {
  const path = pathname.replace(/\/$/, '') || '/';
  if (path === '/') return null; // Home — no breadcrumbs
  if (EXCLUDED_PREFIXES.some(p => path === p || path.startsWith(p + '/'))) return null;

  const origin = window.location.origin;
  const crumbs = [{ name: 'Home', url: origin + '/' }];
  const parts = path.split('/').filter(Boolean);

  // Special handling: /products/<product-slug> — inject category crumb
  if (parts[0] === 'products' && parts.length === 2 && !['shades', 'drapery', 'motorization'].includes(parts[1])) {
    crumbs.push({ name: 'Products', url: origin + '/products' });
    const cat = productCategory(parts[1]);
    if (cat) crumbs.push({ name: cat.name, url: origin + cat.path });
    crumbs.push({ name: LABELS[parts[1]] || slugToTitle(parts[1]) });
    return crumbs;
  }

  // SEO landing pages: /locations/<area>/<topic> sit under Service Area (no /locations index page)
  if (parts[0] === 'locations' && parts.length === 3) {
    crumbs.push({ name: 'Service Area', url: origin + '/service-area' });
    crumbs.push({ name: LOCATION_LABELS[parts[1]] || slugToTitle(parts[1]) });
    return crumbs;
  }

  // General: walk the path segments
  let accumulated = '';
  parts.forEach((segment, i) => {
    accumulated += '/' + segment;
    const isLast = i === parts.length - 1;
    const label = LABELS[segment] || slugToTitle(segment);
    crumbs.push(isLast ? { name: label } : { name: label, url: origin + accumulated });
  });

  return crumbs;
};

const slugToTitle = (slug) =>
  slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/* ------------------------------------------------------------------ */
/*  JSON-LD injection hook                                             */
/* ------------------------------------------------------------------ */
const ATTR = 'data-breadcrumb-ld';

export const useBreadcrumbSchema = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const crumbs = buildCrumbs(pathname);
    let el = document.head.querySelector(`script[${ATTR}]`);

    if (!crumbs || crumbs.length < 2) {
      el?.remove();
      return;
    }

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: crumbs.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: c.name,
        ...(c.url ? { item: c.url } : {}),
      })),
    };

    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute(ATTR, '1');
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(schema);

    return () => el?.remove();
  }, [pathname]);
};

/**
 * Drop-in component: <BreadcrumbSchema />
 * Injects BreadcrumbList JSON-LD for the current route. Renders nothing.
 */
export const BreadcrumbSchema = () => { useBreadcrumbSchema(); return null; };
